import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import {
  Box,
  Container,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import type { ReactCodeInputProps } from "react-code-input";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import type { ValidateShareCodeV1Response } from "@thalamos/common";

import ThalamosLogo from "../assets/img/thalamos_logo.svg";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
  ToastNotifications,
} from "~/components/design-system/index.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";
import ReactCodeInput from "~/lib/compat/reactCodeInput.js";
import { routeFns } from "~/routePaths.js";

type CodeInputGridProps = {
  code: string | null;
  setCode: Dispatch<SetStateAction<string | null>>;
  isMobileView: boolean;
  fields: number;
  type: "shared" | "guest";
  onSubmit?: () => void;
};

export function CodeInputGrid({
  code,
  setCode,
  isMobileView,
  fields,
  type,
  onSubmit,
}: CodeInputGridProps) {
  const commonStyles = {
    fontFamily: "monospace",
    fontWeight: 400,
    width: "3rem",
    height: "3rem",
    fontSize: "1.25rem",
    borderRadius: "0.375rem",
    border: "1px solid #ccc",
    textAlign: "center" as const,
    backgroundColor: "white",
    margin: "0.25rem",
  };

  return (
    <Stack
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "flex-start",
          flexWrap: "wrap",
          minWidth: "180px",
          maxWidth: isMobileView
            ? type === "guest"
              ? "200px"
              : "250px"
            : "unset",
          textAlign: "left",
        }}
      >
        <div
          role="group"
          onKeyDown={(e) => {
            if (e.key === "Enter" && code?.length === fields && onSubmit) {
              onSubmit();
            }
          }}
        >
          <ReactCodeInput
            type="text"
            pattern="[A-Za-z0-9]*"
            fields={fields}
            value={code ?? undefined}
            onChange={(val) => {
              setCode(val.toUpperCase());
            }}
            inputMode={"text" as ReactCodeInputProps["inputMode"]}
            inputStyle={commonStyles}
            name="code"
          />
        </div>
      </Box>
    </Stack>
  );
}

export function EMHFormsShareCodePage({}: {}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const shareCodeFromParams = searchParams.get("shareCode");

  const [shareCode, setShareCode] = useState(shareCodeFromParams);
  const [submitting, setSubmitting] = useState(false);

  const [validatedShareCodeResponse, setValidatedShareCodeResponse] = useState<
    | Omit<ValidateShareCodeV1Response, "incidentId">
    | ValidateShareCodeV1Response
    | null
  >(null);

  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { isMobileView } = useScreenDetection();

  useEffect(() => {
    if (
      validatedShareCodeResponse &&
      "incidentId" in validatedShareCodeResponse
    ) {
      navigate(
        routeFns.contextSelector(
          routeFns.visionPreviewIncident(validatedShareCodeResponse.incidentId),
        ),
      );
    }
  }, [validatedShareCodeResponse]);

  const reset = () => {
    setValidatedShareCodeResponse(null);
    setShareCode("");
  };

  const handleSubmitShareCode = async () => {
    if (submitting || !shareCode) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.vision.validateShareCode(shareCode);

      if (response.status === 200 && response.data.warning !== true) {
        setValidatedShareCodeResponse(response.data);
      } else {
        setError((response.data as any).reason);
      }
    } catch (e) {
      setError((e as any).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          boxShadow: 1,
          width: "100%",
          maxWidth: 580,
          backgroundColor: theme.palette.background.lightGray,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <img
            src={ThalamosLogo}
            alt="Thalamos Logo"
            style={{
              height: "100px",
              verticalAlign: "middle",
              marginLeft: "4px",
            }}
          />
        </Box>

        {!validatedShareCodeResponse && (
          <>
            <Typography
              sx={{
                textAlign: "left",
                mb: 3,
              }}
            >
              {t("pages.shareCodePage.title")}
            </Typography>

            <Typography
              sx={{
                textAlign: "left",
                mb: 3,
                fontWeight: 500,
              }}
            >
              {t("pages.shareCodePage.incidentCodeLabel")}
            </Typography>

            <CodeInputGrid
              code={shareCode}
              setCode={setShareCode}
              isMobileView={isMobileView}
              fields={8}
              type="shared"
              onSubmit={() => {
                void handleSubmitShareCode();
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                disabled={submitting || !shareCode}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={handleSubmitShareCode}
                label="Continue"
                testId="code-submit-button"
              />
            </Box>

            {error && <Banner bannerType={BannerList.ERROR} title={error} />}

            <Typography
              variant="caption"
              align="center"
              sx={{
                mt: 4,
                display: "block",
              }}
            >
              {t("pages.shareCodePage.accessingCodeLinkLabel")}{" "}
              <Link
                target="_blank"
                rel="noopener noreferrer"
                to="https://thalamos1.zohodesk.eu/portal/en/kb/mh-forms-quick-reference-guides"
              >
                {t("common.clickHere")}
              </Link>
            </Typography>
          </>
        )}

        {validatedShareCodeResponse &&
          !("incidentId" in validatedShareCodeResponse) && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                try {
                  const response = await api.vision.accessRequest({
                    email: email!,
                    name: name!,
                    shareCode: shareCode!,
                  });

                  if (response.status === 200) {
                    window.location.href = response.data.redirectUrl;
                  } else {
                    renderErrorToast({
                      message: (response.data as any).reason,
                    });
                  }
                } catch (e) {
                  renderErrorToast({
                    message:
                      (e as any)?.response?.data?.reason ?? (e as any).message,
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <Typography
                sx={{
                  mb: 2,
                  textAlign: "left",
                }}
              >
                {t("pages.shareCodePage.confirmPrompt")}
              </Typography>

              <Stack
                sx={{
                  gap: 2,
                }}
              >
                <TextField
                  label={t("common.name")}
                  variant="outlined"
                  value={name || ""}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextField
                  label={t("common.email")}
                  variant="outlined"
                  type="email"
                  value={email || ""}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 4,
                  }}
                >
                  <Button
                    variant="text"
                    label={t("buttonLabels.goBack")}
                    onClick={reset}
                    sx={{ maxWidth: "200px" }}
                  />
                  <Button
                    type="submit"
                    label={t("buttonLabels.submit")}
                    disabled={!email || !name || submitting}
                    sx={{ maxWidth: "200px" }}
                  />
                </Box>
              </Stack>
            </form>
          )}
      </Box>
      <ToastNotifications />
    </Container>
  );
}
